import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { round2, useAppContext } from "./AppContext";
import { useSelectedResult } from "./SelectedResultContext";
import { PRICE_PIPELINE_MODE_HOTEL_API_ONLY } from "../utils/hotelApiPriceOnlyPipeline";
import { resolveHotelAgentHandlingFeeIls } from "../utils/resolveHotelAgentOfferDisplayTotal";
import {
  applyAgentFeeToHotelPricingSnapshot,
  buildLockedUsdToIlsFn,
  ensureHotelOrderPricingSnapshotFx,
} from "../utils/resolveHotelBookingSummaryPricingSsot";
import { resolveCustomerFacingHotelLineIlsFromOrder } from "../utils/hotelBookingPriceSsot";
import { isAgentHrCustomerFacingSsotOrder } from "../utils/resolvePassengerHotelCustomerFacingDisplay";
import { hrDebugTrace } from "../utils/hrDebugFlags";

const OrderContext = createContext();
const CURRENT_HOTEL_ORDER = "currentHotelOrder";

function readCurrentHotelOrderFromStorage() {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const data = localStorage.getItem(CURRENT_HOTEL_ORDER);
    if (!data || data.length === 0) return null;
    return JSON.parse(data);
  } catch (e) {
    try {
      localStorage.removeItem(CURRENT_HOTEL_ORDER);
    } catch {
      /* ignore */
    }
    return null;
  }
}

function resolveHotelOrderPackageId(order) {
  if (!order?.selectedHotelPackage || typeof order.selectedHotelPackage !== "object") {
    return null;
  }
  const p = order.selectedHotelPackage;
  const id = p.packageID ?? p.PackageId ?? p.packageId ?? null;
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

/**
 * Lightweight fingerprint for dynamic-package bootstrap / flight-sync no-op skips.
 * Intentionally ignores large media blobs on the draft order.
 */
function economyComparableFingerprint(order) {
  if (!order) return "__order_nil__";
  try {
    const hid =
      order.selectedHotel?.HotelId ??
      order.selectedHotel?.hotelId ??
      order.selectedHotel?.HotelID ??
      order.selectedHotel?.id ??
      null;
    const roomId =
      order.selectedRoom?.id ??
      order.selectedRoom?.roomId ??
      order.selectedRoom?.RoomId ??
      null;
    const pkgId =
      order.selectedHotelPackage?.packageID ??
      order.selectedHotelPackage?.PackageId ??
      order.selectedHotelPackage?.packageId ??
      null;
    const oo = order.selectedHotelsRiverPackageOption;
    const opt = oo
      ? {
          roomId: oo.roomId ?? null,
          rateId: oo.rateId ?? null,
          supplierPackageId: oo.supplierPackageId ?? null,
          finalPackagePrice: oo.finalPackagePrice ?? null,
          selectedFlightId: oo.selectedFlightId ?? null,
        }
      : null;
    const op = order.ourPrice || null;
    const ours = op
      ? {
          priceWithCommission: op.priceWithCommission ?? null,
          priceWithHandleFee: op.priceWithHandleFee ?? null,
          priceWithCreditCardFee: op.priceWithCreditCardFee ?? null,
          handlingFee: op.handlingFee ?? null,
          handleFeeData: op.handleFeeData ?? null,
          fixedCommissionInputIls:
            op.fixedCommissionInputIls != null ? op.fixedCommissionInputIls : null,
        }
      : null;

    return JSON.stringify({
      dpf: order.dynamicPackageProfileFlow === true,
      hid: hid != null ? String(hid) : null,
      roomId: roomId != null ? String(roomId) : null,
      pkgId: pkgId != null ? String(pkgId) : null,
      bookingSsotIls:
        order.bookingSsotIls != null && Number.isFinite(Number(order.bookingSsotIls))
          ? Math.round(Number(order.bookingSsotIls))
          : null,
      pricePipelineMode: order.pricePipelineMode ?? null,
      opt,
      ours,
    });
  } catch {
    return "__order_fp_err__";
  }
}

export const OrderProvider = ({ children }) => {
  const { getCreditCardFeePercent, isGlobalConstantLoaded } = useAppContext();
  const selectedResultContext = useSelectedResult();
  const ilsToUsd = selectedResultContext?.ilsToUsd || ((value) => value);
  const usdToIls = selectedResultContext?.usdToIls || ((value) => value);
  const currencyRate = selectedResultContext?.currencyRate ?? null;

  const [saveInfoFunc, setSaveInfoFunc] = useState(null);
  const [savingInfo, setSavingInfo] = useState(false);

  const [currentHotelOrder, _setCurrentHotelOrder] = useState(
    readCurrentHotelOrderFromStorage
  );

  const calculateHandleFee = useCallback((ourPrice) => {
    const priceWithCommission = ourPrice.priceWithCommission;
    const handleFeeData = ourPrice.handleFeeData;

    if (!handleFeeData || !handleFeeData.type) return 0;

    if (handleFeeData.type === "%") {
      if (
        handleFeeData.value == null ||
        String(handleFeeData.value).trim() === "" ||
        !Number.isFinite(Number(priceWithCommission))
      ) {
        return 0;
      }
      return (Number(priceWithCommission) * Number(handleFeeData.value)) / 100;
    }

    if (handleFeeData.type === "+") {
      const ilsFixed = ourPrice.fixedCommissionInputIls;
      if (ilsFixed != null && Number.isFinite(Number(ilsFixed))) {
        return parseFloat(ilsToUsd(Number(ilsFixed)));
      }
      if (
        handleFeeData.value == null ||
        String(handleFeeData.value).trim() === ""
      ) {
        return 0;
      }
      return Number(handleFeeData.value);
    }

    return 0;
  }, [ilsToUsd]);

  // Define calcOurTotalPrice next (depends on calculateHandleFee)
  const calcOurTotalPrice = useCallback(
    (currentHotelOrder) => {
      if (currentHotelOrder?.pricePipelineMode === PRICE_PIPELINE_MODE_HOTEL_API_ONLY) {
        const snapHotel =
          currentHotelOrder?.pricingSnapshot?.hotelTotalIls ??
          currentHotelOrder?.selectedPriceSnapshot?.pricingSnapshot?.hotelTotalIls ??
          null;
        const bookingLineSsot = resolveCustomerFacingHotelLineIlsFromOrder(currentHotelOrder);
        const agentHrUsesBookingSsot =
          isAgentHrCustomerFacingSsotOrder(currentHotelOrder) &&
          bookingLineSsot != null;
        const baseIls = Number(
          agentHrUsesBookingSsot
            ? bookingLineSsot
            : snapHotel != null &&
                Number.isFinite(Number(snapHotel)) &&
                Number(snapHotel) > 0
              ? snapHotel
              : currentHotelOrder.bookingSsotIls
        );
        const ceilBase =
          Number.isFinite(baseIls) && baseIls > 0 ? Math.ceil(baseIls) : 0;
        const draftOur = {
          ...(currentHotelOrder?.ourPrice ||
            currentHotelOrder?.selectedHotelPackage?.ourPrice ||
            {}),
        };
        const agentFeeIls = resolveHotelAgentHandlingFeeIls(
          draftOur,
          ceilBase,
          usdToIls
        );
        const priceWithHandleFeeIls = ceilBase + agentFeeIls;
        const creditCardFeeIls =
          priceWithHandleFeeIls * (getCreditCardFeePercent() / 100);
        const priceWithCreditCardFeeIls = round2(
          priceWithHandleFeeIls + creditCardFeeIls
        );
        const commissionUsd =
          typeof ilsToUsd === "function" ? ilsToUsd(ceilBase) : 0;
        const handlingFeeUsd =
          agentFeeIls > 0 && typeof ilsToUsd === "function"
            ? ilsToUsd(agentFeeIls)
            : 0;
        const priceWithHandleFeeUsd = commissionUsd + handlingFeeUsd;
        const creditCardFeeUsd =
          priceWithHandleFeeUsd * (getCreditCardFeePercent() / 100);
        return {
          ...draftOur,
          initialPrice: 0,
          priceWithCommission: commissionUsd,
          priceWithHandleFee: priceWithHandleFeeUsd,
          handlingFee: handlingFeeUsd,
          handlingFeeIls: agentFeeIls,
          agentCommission: 0,
          agentCommissionSource: "hotel_api_price_only_with_agent_fee",
          creditCardFee: creditCardFeeUsd,
          pricePreFinalRound: priceWithHandleFeeUsd,
          priceWithCreditCardFee: priceWithCreditCardFeeIls,
          priceWithCommissionAndCreditCardFee: priceWithCreditCardFeeIls,
          customerDisplayTotalIls: priceWithCreditCardFeeIls,
          originalCurrency: "ILS",
        };
      }
      const baseOurPrice = currentHotelOrder?.ourPrice;
      if (!baseOurPrice) {
        if (!currentHotelOrder.selectedHotelPackage) return null;
      }
      const sourceOurPrice =
        baseOurPrice || currentHotelOrder?.selectedHotelPackage?.ourPrice;
      if (!sourceOurPrice || typeof sourceOurPrice !== "object") {
        return null;
      }
      const ourPrice = { ...sourceOurPrice };

      ourPrice.handlingFee = calculateHandleFee(ourPrice);

      // HR margin is already included in priceWithCommission (unified path from calcBaseCommission).
      // Do not add a second engine commission here — legacy AGENT_COMMISSION% on gross is deprecated for this flow.
      ourPrice.agentCommission = 0;
      ourPrice.agentCommissionSource = "deprecated_not_used_in_order_context";

      ourPrice.priceWithHandleFee =
        ourPrice.priceWithCommission + ourPrice.handlingFee;

      ourPrice.creditCardFee =
        ourPrice.priceWithHandleFee * (getCreditCardFeePercent() / 100);

      ourPrice.priceWithCreditCardFee = round2(
        ourPrice.priceWithHandleFee + ourPrice.creditCardFee
      );

      return ourPrice;
    },
    [calculateHandleFee, getCreditCardFeePercent, ilsToUsd, usdToIls]
  );

  // Now define functions that depend on calcOurTotalPrice
  const setHandleFeeData = useCallback(
    (type, value, meta = {}) => {
      if (!currentHotelOrder) return;
      const baseOurPrice =
        currentHotelOrder.ourPrice ||
        currentHotelOrder.selectedHotelPackage?.ourPrice ||
        {};

      const nextOurPrice = {
        ...baseOurPrice,
      };

      if (
        type === "+" &&
        meta?.fixedCommissionInputIls != null &&
        Number.isFinite(Number(meta.fixedCommissionInputIls))
      ) {
        nextOurPrice.fixedCommissionInputIls = Math.round(
          Number(meta.fixedCommissionInputIls)
        );
      } else if (type === "%") {
        delete nextOurPrice.fixedCommissionInputIls;
      }

      let normalizedValue = Number(value);
      if (!Number.isFinite(normalizedValue) || normalizedValue < 0) {
        normalizedValue = 0;
      }
      if (type === "+") {
        if (
          nextOurPrice.fixedCommissionInputIls != null &&
          Number.isFinite(nextOurPrice.fixedCommissionInputIls)
        ) {
          normalizedValue = parseFloat(
            ilsToUsd(nextOurPrice.fixedCommissionInputIls)
          );
        } else {
          normalizedValue = parseFloat(ilsToUsd(normalizedValue));
        }
      }

      nextOurPrice.handleFeeData = {
        type: type,
        value: normalizedValue,
      };

      const hotelTotalBaseIls =
        Number(currentHotelOrder?.bookingSsotIls) > 0
          ? Math.ceil(Number(currentHotelOrder.bookingSsotIls))
          : Number(currentHotelOrder?.selectedPackageOriginalSnapshot?.pricingSnapshot?.hotelTotalIls) > 0
            ? Math.ceil(
                Number(
                  currentHotelOrder.selectedPackageOriginalSnapshot.pricingSnapshot.hotelTotalIls
                )
              )
            : Number(currentHotelOrder?.selectedPackageOriginalSnapshot?.finalPriceIls) > 0
              ? Math.ceil(Number(currentHotelOrder.selectedPackageOriginalSnapshot.finalPriceIls))
              : Number(currentHotelOrder?.pricingSnapshot?.hotelTotalIls) > 0
                ? Math.ceil(Number(currentHotelOrder.pricingSnapshot.hotelTotalIls))
                : Number(
                      currentHotelOrder?.selectedPriceSnapshot?.pricingSnapshot?.hotelTotalIls
                    ) > 0
                  ? Math.ceil(
                      Number(
                        currentHotelOrder.selectedPriceSnapshot.pricingSnapshot.hotelTotalIls
                      )
                    )
                  : 0;

      const lockedUsdToIls = buildLockedUsdToIlsFn(
        currentHotelOrder?.pricingSnapshot ??
          currentHotelOrder?.selectedPriceSnapshot?.pricingSnapshot,
        usdToIls
      );
      const feeIls = resolveHotelAgentHandlingFeeIls(
        nextOurPrice,
        hotelTotalBaseIls,
        lockedUsdToIls
      );

      const nextOrder = {
        ...currentHotelOrder,
        ourPrice: nextOurPrice,
      };
      const computedOurPrice = calcOurTotalPrice(nextOrder);
      if (computedOurPrice && typeof computedOurPrice === "object") {
        nextOrder.ourPrice = {
          ...computedOurPrice,
          handleFeeData: nextOurPrice.handleFeeData,
          fixedCommissionInputIls: nextOurPrice.fixedCommissionInputIls,
        };
      }

      const withSnapshot = applyAgentFeeToHotelPricingSnapshot(nextOrder, feeIls);
      const pkg = withSnapshot.selectedHotelPackage;
      if (pkg && typeof pkg === "object") {
        withSnapshot.selectedHotelPackage = {
          ...pkg,
          ourPrice: {
            ...(pkg.ourPrice && typeof pkg.ourPrice === "object" ? pkg.ourPrice : {}),
            ...withSnapshot.ourPrice,
          },
          ...(withSnapshot.selectedPriceSnapshot
            ? { selectedPriceSnapshot: withSnapshot.selectedPriceSnapshot }
            : {}),
        };
      }

      if (import.meta.env?.DEV) {
        try {
          // eslint-disable-next-line no-console
          console.info("[HOTEL_AGENT_FEE_SNAPSHOT_WRITE_TRACE]", {
            hotelTotalIls: withSnapshot.pricingSnapshot?.hotelTotalIls ?? null,
            agentFeeIls: withSnapshot.pricingSnapshot?.agentFeeIls ?? null,
            totalIls: withSnapshot.pricingSnapshot?.totalIls ?? null,
            fixedCommissionInputIls:
              withSnapshot.ourPrice?.fixedCommissionInputIls ?? null,
            handleFeeData: withSnapshot.ourPrice?.handleFeeData ?? null,
          });
        } catch {
          /* ignore */
        }
      }

      _setCurrentHotelOrder(withSnapshot);
    },
    [currentHotelOrder, ilsToUsd, calcOurTotalPrice, usdToIls]
  );

  const setCurrentHotelOrder = useCallback(
    (newCurrentHotelOrder) => {
      if (!newCurrentHotelOrder || typeof newCurrentHotelOrder !== "object") return;

      const prevOrder =
        currentHotelOrder && typeof currentHotelOrder === "object"
          ? currentHotelOrder
          : null;
      const oldPkgId = resolveHotelOrderPackageId(prevOrder);
      const newPkgId = resolveHotelOrderPackageId(newCurrentHotelOrder);
      const packageSwitched =
        newPkgId != null &&
        (oldPkgId == null || String(oldPkgId) !== String(newPkgId));

      let merged;
      if (packageSwitched) {
        merged = { ...newCurrentHotelOrder };

        merged.agentMarkup = 0;
        merged.extraCommission = 0;
        merged.manualFee = 0;
        merged.selectedFeeAmount = null;
        merged.selectedFeeType = null;
        merged.paymentSummary = null;
        merged.checkoutSummary = null;

        const pkgOurRaw = newCurrentHotelOrder.selectedHotelPackage?.ourPrice;
        const baseOur =
          pkgOurRaw && typeof pkgOurRaw === "object"
            ? { ...pkgOurRaw }
            : {
                initialPrice: 0,
                priceWithCommission: 0,
                hrCommission: 0,
                agentCommission: 0,
                creditCardFee: 0,
                pricePreFinalRound: 0,
                priceWithCommissionAndCreditCardFee: 0,
              };

        delete baseOur.handleFeeData;
        delete baseOur.fixedCommissionInputIls;
        baseOur.handlingFee = 0;
        baseOur.agentMarkup = 0;
        baseOur.extraCommission = 0;
        baseOur.manualFee = 0;

        const pwccf = baseOur.priceWithCommissionAndCreditCardFee;
        const hasPwccf =
          pwccf != null &&
          Number.isFinite(Number(pwccf)) &&
          Number(pwccf) > 0;

        const ssotRaw =
          merged.bookingSsotIls ??
          merged.selectedPriceSnapshot?.finalPriceIls ??
          null;
        if (
          !hasPwccf &&
          newCurrentHotelOrder.pricePipelineMode !== PRICE_PIPELINE_MODE_HOTEL_API_ONLY &&
          ssotRaw != null &&
          Number.isFinite(Number(ssotRaw)) &&
          Number(ssotRaw) > 0
        ) {
          const usd = ilsToUsd(Number(ssotRaw), false);
          if (Number.isFinite(usd) && usd > 0) {
            baseOur.initialPrice = usd;
            baseOur.priceWithCommission = usd;
            baseOur.pricePreFinalRound = usd;
            baseOur.priceWithCommissionAndCreditCardFee = usd;
          }
        }

        merged.ourPrice = baseOur;

        try {
          // eslint-disable-next-line no-console
          console.error("[HOTEL_ONLY_ORDER_RESET_TRACE]", {
            oldPackageId: oldPkgId,
            newPackageId: newPkgId,
            packageSwitched,
            oldRootOurPrice: prevOrder?.ourPrice ?? null,
            newSelectedPackageOurPrice:
              newCurrentHotelOrder.selectedHotelPackage?.ourPrice ?? null,
            oldHandlingFee: prevOrder?.ourPrice?.handlingFee ?? null,
            resetApplied: true,
            finalBookingSsotIls:
              merged.bookingSsotIls ??
              merged.selectedPriceSnapshot?.finalPriceIls ??
              null,
          });
        } catch (_) {
          /* ignore */
        }
      } else {
        merged = {
          ...(prevOrder || {}),
          ...newCurrentHotelOrder,
        };
      }

      const computedOurPrice = calcOurTotalPrice(merged);
      merged.ourPrice = computedOurPrice;

      if (packageSwitched) {
        try {
          const incomingPkgOur = newCurrentHotelOrder.selectedHotelPackage?.ourPrice;
          hrDebugTrace("[HOTEL_AGENT_HR_MARGIN_AFTER_RESET_TRACE]", {
            packageId: newPkgId,
            displayCurrenciesIls:
              merged.selectedHotelPackage?.displayCurrencies?.ils ?? null,
            selectedPackagePwccf:
              merged.selectedHotelPackage?.ourPrice?.priceWithCommissionAndCreditCardFee ??
              null,
            bookingSsotIls:
              merged.bookingSsotIls ??
              merged.selectedPriceSnapshot?.finalPriceIls ??
              null,
            rootOurPriceAfterReset: merged.ourPrice,
            source: "order_context_package_switch",
            usedExistingRichOurPrice: Boolean(
              incomingPkgOur && typeof incomingPkgOur === "object"
            ),
            fallbackRawBuilderUsed: !(
              incomingPkgOur && typeof incomingPkgOur === "object"
            ),
          });
        } catch (_) {
          /* ignore */
        }
      }

      const prevFp = economyComparableFingerprint(prevOrder);
      const nextFp = economyComparableFingerprint(merged);
      if (prevFp === nextFp && prevOrder) {
        return;
      }
      _setCurrentHotelOrder(merged);
    },
    [currentHotelOrder, calcOurTotalPrice, ilsToUsd]
  );

  // Helper function to create a lightweight version of the order for localStorage
  const createStorableOrder = (order) => {
    if (!order) return null;

    // Create a lightweight copy excluding large data arrays
    const lightOrder = { ...order };

    // Remove large data that can be refetched
    if (lightOrder.selectedRoom) {
      lightOrder.selectedRoom = {
        ...lightOrder.selectedRoom,
        images: [], // Remove images array
        facilities: lightOrder.selectedRoom.facilities
          ? lightOrder.selectedRoom.facilities.slice(0, 5)
          : [], // Keep only first 5 facilities
      };
    }

    if (lightOrder.selectedHotel) {
      lightOrder.selectedHotel = {
        ...lightOrder.selectedHotel,
        images: [], // Remove hotel images
        facilities: lightOrder.selectedHotel.facilities
          ? lightOrder.selectedHotel.facilities.slice(0, 5)
          : [], // Keep only first 5 facilities
      };
    }

    return lightOrder;
  };

  useEffect(() => {
    if (!currentHotelOrder) return;
    const withFx = ensureHotelOrderPricingSnapshotFx(currentHotelOrder, {
      usdToIls,
      currencyRate,
      adminUsdRate: currencyRate?.USD ?? null,
      adminEurRate: currencyRate?.EUR ?? null,
      adminGbpRate: currencyRate?.GBP ?? null,
    });
    if (withFx !== currentHotelOrder) {
      _setCurrentHotelOrder(withFx);
    }
  }, [
    currentHotelOrder?.bookingSsotIls,
    currentHotelOrder?.selectedHotelPackage?.displayCurrencies?.usd,
    currentHotelOrder?.selectedHotelPackage?.displayCurrencies?.eur,
    currentHotelOrder?.pricingSnapshot?.lockedExchangeRateToIls,
  ]);

  useEffect(() => {
    if (!currentHotelOrder) {
      localStorage.removeItem(CURRENT_HOTEL_ORDER);
      return;
    }

    try {
      // Save only essential data to localStorage
      const storableOrder = createStorableOrder(currentHotelOrder);
      localStorage.setItem(CURRENT_HOTEL_ORDER, JSON.stringify(storableOrder));
    } catch (error) {
      console.warn("Failed to save order to localStorage:", error);
      // If still too large, save only the most essential data
      try {
        const minimalOrder = {
          searchPhrase: currentHotelOrder.searchPhrase,
          selectedHotelPackage: currentHotelOrder.selectedHotelPackage,
          checkInOutInfo: currentHotelOrder.checkInOutInfo,
          ourPrice: currentHotelOrder.ourPrice,
          bookingSsotIls:
            currentHotelOrder.bookingSsotIls != null
              ? currentHotelOrder.bookingSsotIls
              : null,
          selectedPriceSnapshot:
            currentHotelOrder.selectedPriceSnapshot &&
            typeof currentHotelOrder.selectedPriceSnapshot === "object"
              ? {
                  finalPriceIls:
                    currentHotelOrder.selectedPriceSnapshot.finalPriceIls ?? null,
                  source: currentHotelOrder.selectedPriceSnapshot.source ?? null,
                  selectedPackageId:
                    currentHotelOrder.selectedPriceSnapshot.selectedPackageId ??
                    null,
                  bookingSsotTrace:
                    currentHotelOrder.selectedPriceSnapshot.bookingSsotTrace ??
                    null,
                  pricingSnapshot:
                    currentHotelOrder.selectedPriceSnapshot.pricingSnapshot ??
                    currentHotelOrder.pricingSnapshot ??
                    null,
                }
              : null,
          pricingSnapshot: currentHotelOrder.pricingSnapshot ?? null,
          pricePipelineMode: currentHotelOrder.pricePipelineMode ?? null,
          selectedPackageOriginalSnapshot:
            currentHotelOrder.selectedPackageOriginalSnapshot ?? null,
        };
        localStorage.setItem(CURRENT_HOTEL_ORDER, JSON.stringify(minimalOrder));
      } catch (minimalError) {
        console.error("Failed to save even minimal order data:", minimalError);
        localStorage.removeItem(CURRENT_HOTEL_ORDER);
      }
    }
  }, [currentHotelOrder]);

  useEffect(() => {
    if (!currentHotelOrder || !isGlobalConstantLoaded) return;
    const preservedFee = currentHotelOrder?.ourPrice?.handleFeeData;
    const preservedFixedIls = currentHotelOrder?.ourPrice?.fixedCommissionInputIls;
    if (
      preservedFee?.type === "+" &&
      preservedFixedIls == null &&
      Number(currentHotelOrder?.pricingSnapshot?.agentFeeIls) > 0
    ) {
      return;
    }
    const updatedPrice = calcOurTotalPrice(currentHotelOrder);
    if (!updatedPrice || typeof updatedPrice !== "object") return;

    const mergedOurPrice = {
      ...updatedPrice,
      ...(preservedFee?.type ? { handleFeeData: preservedFee } : {}),
      ...(preservedFixedIls != null && Number.isFinite(Number(preservedFixedIls))
        ? { fixedCommissionInputIls: Math.round(Number(preservedFixedIls)) }
        : {}),
    };

    if (
      JSON.stringify(currentHotelOrder.ourPrice) === JSON.stringify(mergedOurPrice)
    ) {
      return;
    }

    let updatedOrder = { ...currentHotelOrder, ourPrice: mergedOurPrice };
    const hotelBase =
      Number(updatedOrder.bookingSsotIls) > 0
        ? Math.ceil(Number(updatedOrder.bookingSsotIls))
        : Number(updatedOrder.selectedPackageOriginalSnapshot?.pricingSnapshot?.hotelTotalIls) > 0
          ? Math.ceil(
              Number(updatedOrder.selectedPackageOriginalSnapshot.pricingSnapshot.hotelTotalIls)
            )
          : Number(updatedOrder.selectedPackageOriginalSnapshot?.finalPriceIls) > 0
            ? Math.ceil(Number(updatedOrder.selectedPackageOriginalSnapshot.finalPriceIls))
            : Number(updatedOrder.selectedPriceSnapshot?.pricingSnapshot?.hotelTotalIls) > 0
              ? Math.ceil(Number(updatedOrder.selectedPriceSnapshot.pricingSnapshot.hotelTotalIls))
              : Number(updatedOrder.pricingSnapshot?.hotelTotalIls) > 0
                ? Math.ceil(Number(updatedOrder.pricingSnapshot.hotelTotalIls))
                : 0;
    const lockedUsdToIls = buildLockedUsdToIlsFn(
      updatedOrder.pricingSnapshot ??
        updatedOrder.selectedPriceSnapshot?.pricingSnapshot,
      usdToIls
    );
    const feeIls = resolveHotelAgentHandlingFeeIls(
      mergedOurPrice,
      hotelBase,
      lockedUsdToIls
    );
    if (feeIls > 0 || updatedOrder.pricingSnapshot?.agentFeeIls != null) {
      updatedOrder = applyAgentFeeToHotelPricingSnapshot(updatedOrder, feeIls);
    }
    _setCurrentHotelOrder(updatedOrder);
  }, [isGlobalConstantLoaded, currentHotelOrder, calcOurTotalPrice, usdToIls]);

  const removeCurrentHotelOrder = useCallback(() => {
    _setCurrentHotelOrder(null);
    localStorage.removeItem(CURRENT_HOTEL_ORDER);
  }, []);

  // Function to clear localStorage if it gets too full
  const clearStorageIfNeeded = useCallback(() => {
    try {
      // Test if we can write to localStorage
      const testKey = "storage_test";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
    } catch (error) {
      console.warn("localStorage is full, clearing hotel order data");
      localStorage.removeItem(CURRENT_HOTEL_ORDER);
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      currentHotelOrder,
      setCurrentHotelOrder,
      removeCurrentHotelOrder,
      setHandleFeeData,
      handleFeeData: currentHotelOrder?.ourPrice?.handleFeeData,
      saveInfoFunc,
      setSaveInfoFunc,
      setSavingInfo,
      savingInfo,
      clearStorageIfNeeded,
      calcOurTotalPrice,
    }),
    [
      currentHotelOrder,
      setCurrentHotelOrder,
      removeCurrentHotelOrder,
      setHandleFeeData,
      currentHotelOrder?.ourPrice?.handleFeeData,
      saveInfoFunc,
      setSaveInfoFunc,
      setSavingInfo,
      savingInfo,
      clearStorageIfNeeded,
      calcOurTotalPrice,
    ]
  );

  return (
    <OrderContext.Provider value={contextValue}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error("useOrder must be used within a OrderProvider");
  }
  return context;
};

export default OrderContext;
